import { isValueNullish } from "../../../../utils";
import template from "./decimal-degree-minute-field.html";
import "./decimal-degree-minute-field.less";

const MAX_DECIMAL_MINUTES = 59.99;
const MAX_MINUTES_LENGTH = 5;

export class Controller {
	constructor() {
		this.isDecimalMinutes = /^\d{0,2}(\.\d{0,2})?$/;
		this.isWholeDegrees = /^\d*$/;
	}

	$onInit() {
		this.trackFieldValue();
	}

	$doCheck() {
		if(this._trackedFieldValue !== this.fieldValue) {
			this.trackFieldValue();
		}
	}

	trackFieldValue() {
		this._trackedFieldValue = this.fieldValue;
		this.lastValidDegrees = this.getValidBoundValue(this.fieldValue?.degrees, this.isWholeDegrees);
		this.lastValidMinutes = this.isValidMinutesValue(this.fieldValue?.minutes)
			? this.fieldValue.minutes
			: "";
	}

	getValidBoundValue(value, pattern) {
		return !isValueNullish(value) && pattern.test(value.toString()) ? value : "";
	}

	handleMinutesChanged(newValue) {
		this.fieldValue.minutes = this.isValidMinutesValue(newValue) ? newValue : this.lastValidMinutes;
		this.lastValidMinutes = this.fieldValue.minutes;
		this.checkDirty();
	}

	isValidMinutesValue(value) {
		if(value === "" || value === ".") {
			return true;
		}

		if(isValueNullish(value) || !this.isDecimalMinutes.test(value.toString())) {
			return false;
		}

		const minutes = Number(value);
		return Number.isFinite(minutes)
			&& minutes <= MAX_DECIMAL_MINUTES
			&& (Number(this.fieldValue?.degrees) !== this.getMaximumDegrees() || minutes === 0);
	}

	handleDegreesChanged(newValue) {
		this.fieldValue.degrees = this.onNumberChanged(newValue, this.lastValidDegrees, this.isWholeDegrees);

		const maximum = this.getMaximumDegrees();
		if(this.fieldValue.degrees > maximum) {
			this.fieldValue.degrees = maximum;
		}
		if(Number(this.fieldValue.degrees) === maximum && Number(this.fieldValue.minutes) > 0) {
			this.fieldValue.minutes = 0;
			this.lastValidMinutes = 0;
		}

		this.lastValidDegrees = this.fieldValue.degrees;
		this.checkDirty();
	}

	onNumberChanged(newValue, lastValidValue, pattern) {
		if(newValue === "") {
			return "";
		}

		if(typeof newValue === "string" && pattern.test(newValue)) {
			return newValue;
		}

		return lastValidValue;
	}

	handleNumberKeyDown(event, allowDecimal) {
		if(!allowDecimal && (event.key === "-" || event.key === "+")) {
			event.preventDefault();
			this.handleSignedDegreeInput(event.key === "-");
			return;
		}

		const editingKeys = ["Backspace", "Delete", "Tab", "Enter", "ArrowLeft", "ArrowRight", "Home", "End"];
		if(event.ctrlKey || event.metaKey || event.altKey || editingKeys.includes(event.key)) {
			return;
		}

		const isDigit = /^\d$/.test(event.key);
		const isDecimal = allowDecimal && event.key === ".";
		if(allowDecimal && (isDigit || isDecimal)) {
			if(!this.isValidMinutesValue(this.getNextInputValue(event.target, event.key))) {
				event.preventDefault();
			}
			return;
		}

		if(!isDigit) {
			event.preventDefault();
		}
	}

	getNextInputValue(input, insertedValue) {
		const selectionStart = input.selectionStart ?? input.value.length;
		const selectionEnd = input.selectionEnd ?? input.value.length;
		return input.value.slice(0, selectionStart) + insertedValue + input.value.slice(selectionEnd);
	}

	handleSignedDegreeInput(isNegative) {
		if(Boolean(this.fieldValue.isNegative) !== isNegative) {
			this.swapCompass();
		}

		this.compassAttentionSequence = (this.compassAttentionSequence ?? 0) + 1;
	}

	getCompassAttentionClass() {
		if(!this.compassAttentionSequence) {
			return "";
		}

		return this.compassAttentionSequence % 2 === 0
			? "compass-attention-even"
			: "compass-attention-odd";
	}

	handleNumberPaste(event, allowDecimal) {
		const sourceEvent = event.originalEvent ?? event;
		const pastedValue = sourceEvent.clipboardData?.getData("text");
		if(pastedValue === undefined) {
			return;
		}

		const nextValue = this.getNextInputValue(event.target, pastedValue);
		const pattern = allowDecimal ? this.isDecimalMinutes : this.isWholeDegrees;
		const isValid = allowDecimal ? this.isValidMinutesValue(nextValue) : pattern.test(nextValue);
		if(!isValid) {
			event.preventDefault();
		}
	}

	handleBlur() {
		this.forcePositive();
		this.clampMinutes();
		this.lastValidDegrees = this.fieldValue.degrees ?? "";
		this.lastValidMinutes = this.fieldValue.minutes ?? "";
		this.checkDirty();
	}

	clampMinutes() {
		if(this.fieldValue.minutes === "" || this.fieldValue.minutes === undefined) {
			return;
		}

		const minutes = Number(this.fieldValue.minutes);
		if(!Number.isFinite(minutes)) {
			return;
		}

		this.fieldValue.minutes = Number(this.fieldValue.degrees) === this.getMaximumDegrees()
			? 0
			: Math.min(MAX_DECIMAL_MINUTES, Math.max(0, minutes));
	}

	forcePositive() {
		if(this.fieldValue.degrees === undefined || this.fieldValue.degrees === "") {
			return;
		}

		if(this.fieldValue.degrees < 0) {
			this.fieldValue.isNegative = !this.fieldValue.isNegative;
		}

		this.fieldValue.degrees = Math.abs(this.fieldValue.degrees);
		if(this.fieldValue.minutes !== undefined && this.fieldValue.minutes !== "") {
			this.fieldValue.minutes = Math.abs(this.fieldValue.minutes);
		}
	}

	checkDirty() {
		if(this.setFieldDirty) {
			this.setFieldDirty();
		}
	}

	swapCompass() {
		this.fieldValue.isNegative = !this.fieldValue.isNegative;
		this.checkDirty();
	}

	getMaximumDegreeDigits() {
		return this.getMaximumDegrees().toString().length;
	}

	getMaximumMinutesLength() {
		return MAX_MINUTES_LENGTH;
	}

	getMaximumDegrees() {
		return this.fieldValue.isLatitude ? 90 : 180;
	}

	getValidationStyling() {
		return this.field.hasValidationError ? "validation-error"
			: this.field.hasValidationWarning ? "validation-warning" : "";
	}

	getCoordinates() {
		if(this.fieldValue?.degrees === undefined || this.fieldValue?.minutes === undefined
			|| isNaN(this.fieldValue.degrees) || isNaN(this.fieldValue.minutes)) {
			return "--empty--";
		}

		return `${this.fieldValue.degrees}° ${this.fieldValue.minutes}'`;
	}
}

export default angular
	.module("vesl.components.report-form.decimal-degree-minute-field", [])
	.component("decimalDegreeMinuteField", {
		template,
		controller: Controller,
		bindings: {
			field: "<",
			fieldValue: "=",
			compassLetter: "<",
			setFieldDirty: "="
		}
	}).name;
